import { supabase } from '../lib/supabase'
import {
  FinanceiroPelArquivoJaProcessadoError,
  type BeginPelFolderInput,
  type CompletePelFolderInput,
  type FinanceiroPelFileStatus,
  type FinanceiroPelStatement,
  type FinanceiroPelStatementFile,
  type FinanceiroPelStatementFolder,
  type FinanceiroPelStatementsConsultaFilters,
  type FinanceiroPelStatementsConsultaResult,
  type RegisterPelFileInput,
  type UpdatePelFileStatusInput,
} from '../types/financeiroExtratosConta'

const FOLDERS_TABLE = 'finan_pel_statement_folders'
const FILES_TABLE = 'finan_pel_statement_files'
const STATEMENTS_TABLE = 'finan_pel_statements'

const STATEMENT_SORT_COLUMNS = new Set([
  'transaction_at',
  'account',
  'type',
  'amount',
  'description',
  'observation',
  'identification',
  'file_name',
  'year',
  'month',
  'day',
  'created_at',
])

function escapeIlike(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_')
}

function parseFolderIso(iso: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (!Number.isInteger(year) || month < 1 || month > 12 || day < 1 || day > 31) return null
  return { year, month, day }
}

/** "7" e "07": a pasta pode ter sido gravada com ou sem zero à esquerda. */
function folderPartVariants(value: number): string[] {
  const padded = String(value).padStart(2, '0')
  const raw = String(value)
  return padded === raw ? [padded] : [padded, raw]
}

/**
 * Limite inferior ou superior da data da pasta (year/month/day).
 * Compara texto com zero à esquerda e sem ele, sem coluna gerada.
 */
function folderDateBoundOr(iso: string, bound: 'from' | 'to'): string | null {
  const parts = parseFolderIso(iso)
  if (!parts) return null
  const { year, month, day } = parts
  const clauses: string[] = [bound === 'from' ? `year.gt.${year}` : `year.lt.${year}`]

  const monthStart = bound === 'from' ? month + 1 : 1
  const monthEnd = bound === 'from' ? 12 : month - 1
  for (let m = monthStart; m <= monthEnd; m += 1) {
    for (const monthValue of folderPartVariants(m)) {
      clauses.push(`and(year.eq.${year},month.eq.${monthValue})`)
    }
  }

  const dayStart = bound === 'from' ? day : 1
  const dayEnd = bound === 'from' ? 31 : day
  for (let d = dayStart; d <= dayEnd; d += 1) {
    for (const monthValue of folderPartVariants(month)) {
      for (const dayValue of folderPartVariants(d)) {
        clauses.push(`and(year.eq.${year},month.eq.${monthValue},day.eq.${dayValue})`)
      }
    }
  }

  return clauses.join(',')
}

/** Evita heal de arquivo ainda em voo (n8n acabou de começar a gravar). */
const HEAL_MIN_AGE_MS = 30_000
/** Limite seguro para filtros `.in()` do PostgREST. */
const SUPABASE_IN_CHUNK = 100
/**
 * Teto de linhas devolvidas pelo PostgREST.
 * Página cheia não encerra a busca: ainda pode haver ids nas páginas seguintes.
 */
const SUPABASE_PAGE_SIZE = 1000

type PelHealFileRow = Pick<
  FinanceiroPelStatementFile,
  'id' | 'folder_id' | 'year' | 'month' | 'day' | 'account' | 'file_name'
>

function chunkIds(ids: string[], size: number): string[][] {
  if (ids.length === 0) return []
  const out: string[][] = []
  for (let i = 0; i < ids.length; i += size) {
    out.push(ids.slice(i, i + size))
  }
  return out
}

function readId(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

async function listStaleProcessingFiles(
  userId: string,
  staleBefore: string
): Promise<PelHealFileRow[]> {
  const all: PelHealFileRow[] = []
  let from = 0
  for (;;) {
    const { data, error } = await supabase
      .from(FILES_TABLE)
      .select('id, folder_id, year, month, day, account, file_name')
      .eq('status', 'processing')
      .eq('processed_by', userId)
      .lt('processed_at', staleBefore)
      .order('id', { ascending: true })
      .range(from, from + SUPABASE_PAGE_SIZE - 1)

    if (error) throw error
    const rows = (data ?? []) as PelHealFileRow[]
    all.push(...rows)
    if (rows.length < SUPABASE_PAGE_SIZE) break
    from += SUPABASE_PAGE_SIZE
  }
  return all
}

async function listStaleProcessingFolderIds(
  userId: string,
  staleBefore: string
): Promise<string[]> {
  const ids: string[] = []
  let from = 0
  for (;;) {
    const { data, error } = await supabase
      .from(FOLDERS_TABLE)
      .select('id')
      .eq('status', 'processing')
      .eq('processed_by', userId)
      .lt('processed_at', staleBefore)
      .order('id', { ascending: true })
      .range(from, from + SUPABASE_PAGE_SIZE - 1)

    if (error) throw error
    const rows = (data ?? []) as { id: string }[]
    for (const row of rows) {
      const id = readId(row.id)
      if (id) ids.push(id)
    }
    if (rows.length < SUPABASE_PAGE_SIZE) break
    from += SUPABASE_PAGE_SIZE
  }
  return ids
}

/**
 * Arquivos do lote que já têm ao menos uma linha em finan_pel_statements.
 * Segue o range enquanto a página voltar cheia.
 */
async function fileIdsWithStatements(fileIds: string[]): Promise<Set<string>> {
  const found = new Set<string>()
  for (const chunk of chunkIds(fileIds, SUPABASE_IN_CHUNK)) {
    const pending = new Set(chunk)
    let from = 0
    for (;;) {
      if (pending.size === 0) break
      const { data, error } = await supabase
        .from(STATEMENTS_TABLE)
        .select('id, file_id')
        .in('file_id', chunk)
        .order('id', { ascending: true })
        .range(from, from + SUPABASE_PAGE_SIZE - 1)

      if (error) throw error
      const rows = (data ?? []) as { file_id: unknown }[]
      for (const row of rows) {
        const fileId = readId(row.file_id)
        if (fileId && pending.has(fileId)) {
          found.add(fileId)
          pending.delete(fileId)
        }
      }
      if (rows.length < SUPABASE_PAGE_SIZE) break
      from += SUPABASE_PAGE_SIZE
    }
  }
  return found
}

async function folderIdsPresent(
  folderIds: string[],
  fetchPage: (
    chunk: string[],
    from: number,
    to: number
  ) => Promise<{ data: { folder_id: unknown }[] | null; error: { message: string } | null }>
): Promise<Set<string>> {
  const found = new Set<string>()
  for (const chunk of chunkIds(folderIds, SUPABASE_IN_CHUNK)) {
    const pending = new Set(chunk)
    let from = 0
    for (;;) {
      if (pending.size === 0) break
      const { data, error } = await fetchPage(chunk, from, from + SUPABASE_PAGE_SIZE - 1)
      if (error) throw error
      const rows = data ?? []
      for (const row of rows) {
        const folderId = readId(row.folder_id)
        if (folderId && pending.has(folderId)) {
          found.add(folderId)
          pending.delete(folderId)
        }
      }
      if (rows.length < SUPABASE_PAGE_SIZE) break
      from += SUPABASE_PAGE_SIZE
    }
  }
  return found
}

async function folderIdsWithOkFiles(folderIds: string[]): Promise<Set<string>> {
  return folderIdsPresent(folderIds, async (chunk, from, to) => {
    const { data, error } = await supabase
      .from(FILES_TABLE)
      .select('id, folder_id')
      .in('folder_id', chunk)
      .eq('status', 'ok')
      .order('id', { ascending: true })
      .range(from, to)

    return {
      data: (data ?? null) as { folder_id: unknown }[] | null,
      error,
    }
  })
}

async function folderIdsWithStatements(folderIds: string[]): Promise<Set<string>> {
  return folderIdsPresent(folderIds, async (chunk, from, to) => {
    const { data, error } = await supabase
      .from(STATEMENTS_TABLE)
      .select('id, folder_id')
      .in('folder_id', chunk)
      .order('id', { ascending: true })
      .range(from, to)

    return {
      data: (data ?? null) as { folder_id: unknown }[] | null,
      error,
    }
  })
}

async function countFilesByStatus(
  folderId: string,
  status: FinanceiroPelFileStatus
): Promise<number> {
  const { count, error } = await supabase
    .from(FILES_TABLE)
    .select('id', { count: 'exact', head: true })
    .eq('folder_id', folderId)
    .eq('status', status)

  if (error) throw error
  return count ?? 0
}

/**
 * Pasta sem arquivo `processing` deixa de ser elegível ao delete em cascata.
 */
async function completeFoldersWithoutProcessing(folderIds: string[]): Promise<void> {
  const unique = Array.from(new Set(folderIds.filter((id) => id.length > 0)))
  for (const folderId of unique) {
    const processingCount = await countFilesByStatus(folderId, 'processing')
    if (processingCount > 0) continue

    const filesOk = await countFilesByStatus(folderId, 'ok')
    const filesError = await countFilesByStatus(folderId, 'error')

    const { error } = await supabase
      .from(FOLDERS_TABLE)
      .update({
        status: 'complete',
        files_ok: filesOk,
        files_error: filesError,
        processed_at: new Date().toISOString(),
      })
      .eq('id', folderId)
      .eq('status', 'processing')

    if (error) throw error
  }
}

/** Chave única do dia: YYYY/MM/DD */
export function pelFolderKey(year: string, month: string, day: string): string {
  return `${year}/${month}/${day}`
}

/** Chave única do arquivo: YYYY/MM/DD/conta/arquivo */
export function pelFileKey(
  year: string,
  month: string,
  day: string,
  account: string,
  fileName: string
): string {
  return `${year}/${month}/${day}/${account}/${fileName}`
}

export const financeiroExtratosContaService = {
  /** Arquivos com status=ok (não reprocessar). */
  async listArquivosProcessadosOk(): Promise<FinanceiroPelStatementFile[]> {
    const { data, error } = await supabase
      .from(FILES_TABLE)
      .select('*')
      .eq('status', 'ok')
      .order('processed_at', { ascending: false })

    if (error) throw error
    return (data ?? []) as FinanceiroPelStatementFile[]
  },

  async isArquivoProcessadoOk(
    year: string,
    month: string,
    day: string,
    account: string,
    fileName: string
  ): Promise<boolean> {
    const { data, error } = await supabase
      .from(FILES_TABLE)
      .select('id')
      .eq('year', year)
      .eq('month', month)
      .eq('day', day)
      .eq('account', account)
      .eq('file_name', fileName)
      .eq('status', 'ok')
      .maybeSingle()

    if (error) throw error
    return !!data
  },

  /**
   * Arquivos `processing` do próprio usuário com linhas em finan_pel_statements
   * (front não confirmou ok, mas o n8n já gravou). Heurística: existência de
   * statement + idade mínima — gravação parcial do n8n ainda pode gerar falso ok.
   * Sem userId não altera nada (mesmo critério de cleanupIncompleteFolders).
   */
  async healProcessingFilesWithStatements(
    userId: string | null | undefined
  ): Promise<string[]> {
    if (!userId) return []

    const staleBefore = new Date(Date.now() - HEAL_MIN_AGE_MS).toISOString()
    const processing = await listStaleProcessingFiles(userId, staleBefore)
    if (processing.length === 0) return []

    const healedIdSet = await fileIdsWithStatements(processing.map((f) => f.id))
    if (healedIdSet.size === 0) return []

    const healedIds = Array.from(healedIdSet)
    const updatedIds = new Set<string>()
    for (const chunk of chunkIds(healedIds, SUPABASE_IN_CHUNK)) {
      const { data, error: updateError } = await supabase
        .from(FILES_TABLE)
        .update({
          status: 'ok',
          error_message: null,
          processed_at: new Date().toISOString(),
        })
        .in('id', chunk)
        .eq('status', 'processing')
        .eq('processed_by', userId)
        .lt('processed_at', staleBefore)
        .select('id')

      if (updateError) throw updateError
      for (const row of data ?? []) {
        const id = readId(row.id)
        if (id) updatedIds.add(id)
      }
    }

    const healed = processing.filter((f) => updatedIds.has(f.id))
    await completeFoldersWithoutProcessing(healed.map((f) => f.folder_id))

    return healed.map((f) => pelFileKey(f.year, f.month, f.day, f.account, f.file_name))
  },

  /**
   * Remove pastas `processing` órfãs do próprio usuário (ex.: crash antigo).
   * O delete da pasta é CASCADE nos arquivos: pasta com arquivo `ok` ou com
   * linha em finan_pel_statements é concluída, não apagada.
   * Arquivo `processing` com statement também fica de fora do delete.
   */
  async cleanupIncompleteFolders(userId: string | null | undefined): Promise<number> {
    if (!userId) return 0

    const staleBefore = new Date(Date.now() - 60 * 60 * 1000).toISOString()
    const staleFiles = await listStaleProcessingFiles(userId, staleBefore)
    const filesWithStatements = await fileIdsWithStatements(staleFiles.map((f) => f.id))
    const deletableFileIds = staleFiles
      .map((f) => f.id)
      .filter((id) => !filesWithStatements.has(id))

    let filesDeleted = 0
    for (const chunk of chunkIds(deletableFileIds, SUPABASE_IN_CHUNK)) {
      const { data, error } = await supabase
        .from(FILES_TABLE)
        .delete()
        .in('id', chunk)
        .eq('status', 'processing')
        .eq('processed_by', userId)
        .lt('processed_at', staleBefore)
        .select('id')

      if (error) throw error
      filesDeleted += data?.length ?? 0
    }

    const staleFolderIds = await listStaleProcessingFolderIds(userId, staleBefore)
    const protectedFolderIds = new Set<string>()
    for (const id of await folderIdsWithOkFiles(staleFolderIds)) protectedFolderIds.add(id)
    for (const id of await folderIdsWithStatements(staleFolderIds)) protectedFolderIds.add(id)

    const deletableFolderIds = staleFolderIds.filter((id) => !protectedFolderIds.has(id))
    let foldersDeleted = 0
    for (const chunk of chunkIds(deletableFolderIds, SUPABASE_IN_CHUNK)) {
      const { data, error } = await supabase
        .from(FOLDERS_TABLE)
        .delete()
        .in('id', chunk)
        .eq('status', 'processing')
        .eq('processed_by', userId)
        .lt('processed_at', staleBefore)
        .select('id')

      if (error) throw error
      foldersDeleted += data?.length ?? 0
    }

    await completeFoldersWithoutProcessing(Array.from(protectedFolderIds))

    return filesDeleted + foldersDeleted
  },

  /**
   * Abre ou retoma a pasta do dia (folder_key = YYYY/MM/DD) em status=processing.
   * Pastas complete podem ser reabertas para novos arquivos.
   */
  async beginOrResumeFolder(input: BeginPelFolderInput): Promise<FinanceiroPelStatementFolder> {
    const year = input.year.trim()
    const month = input.month.trim()
    const day = input.day.trim()
    if (!year || !month || !day) {
      throw new Error('Ano, mês e dia são obrigatórios.')
    }

    const folderKey = pelFolderKey(year, month, day)

    const { data: existing, error: findError } = await supabase
      .from(FOLDERS_TABLE)
      .select('*')
      .eq('folder_key', folderKey)
      .maybeSingle()

    if (findError) throw findError

    if (existing) {
      const { data: updated, error: updateError } = await supabase
        .from(FOLDERS_TABLE)
        .update({
          status: 'processing',
          root_folder_name: input.rootFolderName,
          processed_by: input.userId,
          processed_at: new Date().toISOString(),
        })
        .eq('id', existing.id)
        .select('*')
        .single()

      if (updateError) throw updateError
      return updated as FinanceiroPelStatementFolder
    }

    const { data: pasta, error: pastaError } = await supabase
      .from(FOLDERS_TABLE)
      .insert({
        folder_key: folderKey,
        year,
        month,
        day,
        root_folder_name: input.rootFolderName,
        files_ok: 0,
        files_error: 0,
        status: 'processing',
        processed_by: input.userId,
        processed_at: new Date().toISOString(),
      })
      .select('*')
      .single()

    if (pastaError) {
      if (pastaError.code === '23505') {
        const { data: raced, error: racedError } = await supabase
          .from(FOLDERS_TABLE)
          .select('*')
          .eq('folder_key', folderKey)
          .single()
        if (racedError) throw racedError
        return raced as FinanceiroPelStatementFolder
      }
      throw pastaError
    }

    return pasta as FinanceiroPelStatementFolder
  },

  /**
   * Cria/atualiza o registro do PDF (ex.: status=processing antes do webhook).
   * Bloqueia se já existir registro com status=ok.
   */
  async registerFile(input: RegisterPelFileInput): Promise<FinanceiroPelStatementFile> {
    const alreadyOk = await this.isArquivoProcessadoOk(
      input.year,
      input.month,
      input.day,
      input.account,
      input.fileName
    )
    if (alreadyOk) {
      throw new FinanceiroPelArquivoJaProcessadoError(
        pelFileKey(input.year, input.month, input.day, input.account, input.fileName)
      )
    }

    const { data: existing, error: findError } = await supabase
      .from(FILES_TABLE)
      .select('id, status')
      .eq('year', input.year)
      .eq('month', input.month)
      .eq('day', input.day)
      .eq('account', input.account)
      .eq('file_name', input.fileName)
      .maybeSingle()

    if (findError) throw findError

    const payload = {
      folder_id: input.folderId,
      year: input.year,
      month: input.month,
      day: input.day,
      account: input.account,
      file_name: input.fileName,
      status: input.status,
      error_message: input.errorMessage ?? null,
      processed_by: input.userId,
      processed_at: new Date().toISOString(),
    }

    if (existing) {
      const { data, error } = await supabase
        .from(FILES_TABLE)
        .update(payload)
        .eq('id', existing.id)
        .select('*')
        .single()
      if (error) throw error
      return data as FinanceiroPelStatementFile
    }

    const { data, error } = await supabase.from(FILES_TABLE).insert(payload).select('*').single()
    if (error) {
      if (error.code === '23505') {
        throw new FinanceiroPelArquivoJaProcessadoError(
          pelFileKey(input.year, input.month, input.day, input.account, input.fileName)
        )
      }
      throw error
    }
    return data as FinanceiroPelStatementFile
  },

  /** Atualiza status do arquivo após resposta do webhook. */
  async updateFileStatus(input: UpdatePelFileStatusInput): Promise<FinanceiroPelStatementFile> {
    const { data, error } = await supabase
      .from(FILES_TABLE)
      .update({
        status: input.status,
        error_message: input.errorMessage ?? null,
        processed_by: input.userId,
        processed_at: new Date().toISOString(),
      })
      .eq('id', input.fileId)
      .select('*')
      .single()

    if (error) throw error
    return data as FinanceiroPelStatementFile
  },

  async completeFolder(input: CompletePelFolderInput): Promise<void> {
    const { data: files, error: listError } = await supabase
      .from(FILES_TABLE)
      .select('status')
      .eq('folder_id', input.folderId)

    if (listError) throw listError

    const filesOk =
      files?.filter((f) => f.status === 'ok').length ?? input.arquivosOk
    const filesError =
      files?.filter((f) => f.status === 'error').length ?? input.arquivosErro

    const { error } = await supabase
      .from(FOLDERS_TABLE)
      .update({
        status: 'complete',
        files_ok: filesOk,
        files_error: filesError,
        processed_at: new Date().toISOString(),
      })
      .eq('id', input.folderId)

    if (error) throw error
  },

  /** Anos distintos das pastas de controle (complete). */
  async listStatementFilterYears(): Promise<string[]> {
    const { data, error } = await supabase
      .from(FOLDERS_TABLE)
      .select('year')
      .eq('status', 'complete')
      .order('year', { ascending: false })

    if (error) throw error
    const years = new Set<string>()
    for (const row of data ?? []) {
      if (row.year) years.add(String(row.year))
    }
    return Array.from(years).sort((a, b) => b.localeCompare(a))
  },

  /** Meses distintos para um ano (ou todos se year vazio). */
  async listStatementFilterMonths(year?: string | null): Promise<string[]> {
    let query = supabase
      .from(FOLDERS_TABLE)
      .select('month')
      .eq('status', 'complete')
      .order('month', { ascending: true })

    if (year?.trim()) {
      query = query.eq('year', year.trim())
    }

    const { data, error } = await query
    if (error) throw error
    const months = new Set<string>()
    for (const row of data ?? []) {
      if (row.month) months.add(String(row.month))
    }
    return Array.from(months).sort((a, b) => a.localeCompare(b))
  },

  /** Dias distintos da pasta para um ano/mês (ou todos se vazios). */
  async listStatementFilterDays(year?: string | null, month?: string | null): Promise<string[]> {
    let query = supabase
      .from(FOLDERS_TABLE)
      .select('day')
      .eq('status', 'complete')
      .order('day', { ascending: true })

    if (year?.trim()) {
      query = query.eq('year', year.trim())
    }
    if (month?.trim()) {
      query = query.eq('month', month.trim())
    }

    const { data, error } = await query
    if (error) throw error
    const days = new Set<string>()
    for (const row of data ?? []) {
      if (row.day) days.add(String(row.day))
    }
    return Array.from(days).sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }))
  },

  /** Contas distintas nos arquivos processados (opcionalmente filtradas por ano/mês/dia). */
  async listStatementFilterAccounts(
    year?: string | null,
    month?: string | null,
    day?: string | null
  ): Promise<string[]> {
    let query = supabase
      .from(FILES_TABLE)
      .select('account')
      .eq('status', 'ok')
      .neq('account', '')
      .order('account', { ascending: true })

    if (year?.trim()) {
      query = query.eq('year', year.trim())
    }
    if (month?.trim()) {
      query = query.eq('month', month.trim())
    }
    if (day?.trim()) {
      query = query.eq('day', day.trim())
    }

    const { data, error } = await query
    if (error) throw error
    const accounts = new Set<string>()
    for (const row of data ?? []) {
      const account = String(row.account ?? '').trim()
      if (account) accounts.add(account)
    }
    return Array.from(accounts).sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }))
  },

  /** Consulta paginada das linhas do extrato (finan_pel_statements). */
  async listStatements(
    filters: FinanceiroPelStatementsConsultaFilters
  ): Promise<FinanceiroPelStatementsConsultaResult> {
    const page = Math.max(1, filters.page)
    const pageSize = Math.max(1, Math.min(100, filters.pageSize))
    const from = (page - 1) * pageSize
    const to = from + pageSize - 1

    const sortBy =
      filters.sortBy && STATEMENT_SORT_COLUMNS.has(filters.sortBy)
        ? filters.sortBy
        : 'transaction_at'
    const ascending = (filters.sortOrder ?? 'desc') === 'asc'

    const hasSelectiveFilter = Boolean(
      filters.year?.trim() ||
        filters.month?.trim() ||
        filters.day?.trim() ||
        filters.account?.trim() ||
        filters.type ||
        filters.dateFrom?.trim() ||
        filters.dateTo?.trim() ||
        filters.folderDateFrom?.trim() ||
        filters.folderDateTo?.trim() ||
        filters.search?.trim()
    )
    const countMode = hasSelectiveFilter ? 'exact' : 'estimated'

    let query = supabase
      .from(STATEMENTS_TABLE)
      .select('*', { count: countMode })

    if (filters.year?.trim()) {
      query = query.eq('year', filters.year.trim())
    }
    if (filters.month?.trim()) {
      query = query.eq('month', filters.month.trim())
    }
    if (filters.day?.trim()) {
      query = query.eq('day', filters.day.trim())
    }
    const folderFrom = folderDateBoundOr(filters.folderDateFrom?.trim() || '', 'from')
    const folderTo = folderDateBoundOr(filters.folderDateTo?.trim() || '', 'to')
    if (folderFrom) query = query.or(folderFrom)
    if (folderTo) query = query.or(folderTo)
    if (filters.account?.trim()) {
      query = query.eq('account', filters.account.trim())
    }
    if (filters.type === 'debit' || filters.type === 'credit') {
      query = query.eq('type', filters.type)
    }
    if (filters.dateFrom?.trim()) {
      query = query.gte('transaction_at', `${filters.dateFrom.trim()}T00:00:00`)
    }
    if (filters.dateTo?.trim()) {
      query = query.lte('transaction_at', `${filters.dateTo.trim()}T23:59:59.999`)
    }

    const search = filters.search?.trim()
    if (search) {
      const term = escapeIlike(search.replace(/,/g, ' '))
      query = query.or(
        [
          `description.ilike.%${term}%`,
          `observation.ilike.%${term}%`,
          `identification.ilike.%${term}%`,
        ].join(',')
      )
    }

    const { data, error, count } = await query
      .order(sortBy, { ascending })
      .order('id', { ascending })
      .range(from, to)

    if (error) {
      throw new Error(error.message || 'Erro ao carregar extratos.')
    }
    return {
      rows: (data ?? []) as FinanceiroPelStatement[],
      total: count ?? 0,
    }
  },

  /**
   * Soma amount por dia no período (pasta OU transaction_at).
   * Requer frontend/sqls/financeiro_09_analise_comparativa.sql.
   */
  async sumAmountByPeriod(input: {
    de: string
    ate: string
    account?: string | null
    identification?: string | null
    type?: 'debit' | 'credit' | null
  }): Promise<Array<{ dia: string; total: number }>> {
    const de = input.de?.trim() || ''
    const ate = input.ate?.trim() || ''
    if (!/^\d{4}-\d{2}-\d{2}$/.test(de) || !/^\d{4}-\d{2}-\d{2}$/.test(ate)) {
      return []
    }

    const { data, error } = await supabase.rpc('finan_pel_soma_extrato_periodo', {
      p_data_de: de,
      p_data_ate: ate,
      p_account: input.account?.trim() || null,
      p_identification: input.identification?.trim() || null,
      p_type:
        input.type === 'debit' || input.type === 'credit' ? input.type : null,
    })

    if (error) {
      const message = error.message || 'Erro ao somar o extrato do período.'
      if (
        /finan_pel_soma_extrato_periodo|Could not find the function|schema cache/i.test(message)
      ) {
        throw new Error(
          'A soma do extrato por período ainda não está disponível. Execute frontend/sqls/financeiro_09_analise_comparativa.sql no Supabase SQL Editor.'
        )
      }
      throw new Error(message)
    }

    return (data ?? [])
      .map((row: { dia?: string | null; total_extrato?: number | string | null }) => ({
        dia: String(row.dia ?? '').slice(0, 10),
        total: Number(row.total_extrato ?? 0) || 0,
      }))
      .filter((row: { dia: string; total: number }) => Boolean(row.dia))
  },
}
